import React, { useState } from "react";
import {
  Alert,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Typography,
  message,
} from "antd";
import { simulatePaymentEvents } from "../paymentHealthApi";
import { SimulatePaymentEventsInput } from "../../../interfaces/paymentHealth.interface";

interface SimulateEventModalProps {
  open: boolean;
  onClose: () => void;
  onSubmitted?: () => void;
}

interface EventPreset {
  value: string;
  label: string;
  defaultCode: string;
  defaultCount: number;
  extra?: Record<string, string | number | boolean>;
}

const CUSTOM = "__custom__";

// Mirrors the events the alerter watches (adminCronJobs alerter.handler.ts).
// Only the BASELINE events open incidents on the status page; the rest fire a
// Slack alert only.
const EVENT_PRESETS: EventPreset[] = [
  {
    value: "yazara_init_error",
    label: "yazara_init_error — Yazara SDK init (opens incident)",
    defaultCode: "YZ_INIT_500",
    defaultCount: 15,
  },
  {
    value: "payment_timeout",
    label: "payment_timeout — Payment timeouts (opens incident)",
    defaultCode: "TIMEOUT",
    defaultCount: 20,
  },
  {
    value: "yazara_register_error",
    label: "yazara_register_error — Slack alert",
    defaultCode: "YZ_REG_500",
    defaultCount: 25,
  },
  {
    value: "$ae_crashed",
    label: "$ae_crashed — App crashes (Slack alert)",
    defaultCode: "",
    defaultCount: 25,
  },
  {
    value: "error_fetching_transactions",
    label: "error_fetching_transactions — Slack alert",
    defaultCode: "FETCH_500",
    defaultCount: 25,
  },
  {
    value: "payment_result",
    label: "payment_result — failure-rate RATIO (needs high volume)",
    defaultCode: "",
    defaultCount: 60,
    extra: { trx_result: "failed" },
  },
  {
    value: CUSTOM,
    label: "Custom event…",
    defaultCode: "",
    defaultCount: 15,
  },
];

const SimulateEventModal: React.FC<SimulateEventModalProps> = ({
  open,
  onClose,
  onSubmitted,
}) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<string>(
    EVENT_PRESETS[0].value
  );

  function onPresetChange(value: string) {
    setSelectedEvent(value);
    const preset = EVENT_PRESETS.find((p) => p.value === value);
    if (preset) {
      form.setFieldsValue({
        errorCode: preset.defaultCode,
        count: preset.defaultCount,
      });
    }
  }

  async function handleSubmit() {
    const values = await form.validateFields();
    const preset = EVENT_PRESETS.find((p) => p.value === values.eventPreset);
    const event =
      values.eventPreset === CUSTOM
        ? (values.customEvent || "").trim()
        : values.eventPreset;
    if (!event) {
      message.error("Enter a custom event name");
      return;
    }

    const input: SimulatePaymentEventsInput = {
      event,
      count: values.count,
      errorCode: values.errorCode?.trim() || undefined,
      merchantCount: values.merchantCount,
      deviceCount: values.deviceCount,
      appVersion: values.appVersion?.trim() || undefined,
      extraProperties: preset?.extra,
    };

    setSubmitting(true);
    try {
      const res = await simulatePaymentEvents(input);
      message.success(
        `Sent ${res.sent} "${res.event}" events (${res.distinctMerchants} merchants, ${res.distinctDevices} devices). Alerter picks them up within ~5 min.`
      );
      onSubmitted?.();
      onClose();
    } catch (err: any) {
      message.error(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to send simulated events"
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title="Simulate payment events (dev only)"
      open={open}
      onCancel={onClose}
      onOk={handleSubmit}
      okText="Send events"
      confirmLoading={submitting}
      destroyOnClose
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Injects synthetic Mixpanel events into the dev project"
        description="The alerter reads them on its next run (≤5 min) and drives incidents / the status page. Events are tagged is_synthetic. Disabled on production."
      />
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          eventPreset: EVENT_PRESETS[0].value,
          errorCode: EVENT_PRESETS[0].defaultCode,
          count: EVENT_PRESETS[0].defaultCount,
          merchantCount: 3,
          deviceCount: 3,
          appVersion: "9.9.9-sim",
        }}
      >
        <Form.Item label="Event" name="eventPreset">
          <Select options={EVENT_PRESETS} onChange={onPresetChange} />
        </Form.Item>

        {selectedEvent === CUSTOM && (
          <Form.Item
            label="Custom event name"
            name="customEvent"
            rules={[{ required: true, message: "Enter an event name" }]}
          >
            <Input placeholder="e.g. processor_init_result" />
          </Form.Item>
        )}

        <Form.Item
          label="Error code"
          name="errorCode"
          tooltip="Stored as processor_error_code / error_code. Leave blank for events without a code (e.g. $ae_crashed)."
        >
          <Input placeholder="e.g. YZ_INIT_500" />
        </Form.Item>

        <Space size="large" style={{ display: "flex" }}>
          <Form.Item
            label="Event count"
            name="count"
            rules={[{ required: true }]}
          >
            <InputNumber min={1} max={500} style={{ width: 120 }} />
          </Form.Item>
          <Form.Item label="Distinct merchants" name="merchantCount">
            <InputNumber min={1} max={100} style={{ width: 120 }} />
          </Form.Item>
          <Form.Item label="Distinct devices" name="deviceCount">
            <InputNumber min={1} max={100} style={{ width: 120 }} />
          </Form.Item>
        </Space>

        <Form.Item label="App version" name="appVersion">
          <Input placeholder="9.9.9-sim" />
        </Form.Item>

        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          Tip: the two “opens incident” events need enough volume to breach the
          baseline floor — the defaults already do.
        </Typography.Text>
      </Form>
    </Modal>
  );
};

export default SimulateEventModal;
